import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { fetchDailyPuzzle } from "@/utils/dailyPuzzle";

type Card = { content: string; position: number };
type Category = { title: string; cards: Card[] };
type Puzzle = { categories: Category[]; print_date: string; id: number };

const DIFFICULTY_COLORS = [
  "bg-[hsl(var(--connections-yellow))]",
  "bg-[hsl(var(--connections-green))]",
  "bg-[hsl(var(--connections-blue))]",
  "bg-[hsl(var(--connections-purple))]",
];

const FALLBACK: Puzzle = {
  print_date: "fallback",
  id: 0,
  categories: [
    { title: "FRUITS", cards: [
      { content: "APPLE", position: 0 }, { content: "PEAR", position: 1 },
      { content: "PLUM", position: 2 }, { content: "KIWI", position: 3 },
    ]},
    { title: "COLORS", cards: [
      { content: "RED", position: 4 }, { content: "BLUE", position: 5 },
      { content: "GREEN", position: 6 }, { content: "PINK", position: 7 },
    ]},
    { title: "DOGS", cards: [
      { content: "PUG", position: 8 }, { content: "BOXER", position: 9 },
      { content: "POODLE", position: 10 }, { content: "HUSKY", position: 11 },
    ]},
    { title: "PLANETS", cards: [
      { content: "MARS", position: 12 }, { content: "VENUS", position: 13 },
      { content: "EARTH", position: 14 }, { content: "SATURN", position: 15 },
    ]},
  ],
};

function getTodayDate() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

async function fetchPuzzle(date: string): Promise<Puzzle> {
  const json = await fetchDailyPuzzle<Puzzle>("group-sort", date);
  if (json?.categories && json.categories.length === 4) return json;
  return FALLBACK;
}

type SavedState = {
  date: string;
  solved: string[];
  mistakes: number;
  status: "playing" | "won" | "lost";
};

function loadState(date: string): SavedState {
  if (typeof window === "undefined") return { date, solved: [], mistakes: 0, status: "playing" };
  try {
    const raw = localStorage.getItem("connections-state");
    if (!raw) return { date, solved: [], mistakes: 0, status: "playing" };
    const parsed = JSON.parse(raw) as SavedState;
    if (parsed.date !== date) return { date, solved: [], mistakes: 0, status: "playing" };
    return parsed;
  } catch {
    return { date, solved: [], mistakes: 0, status: "playing" };
  }
}

const MAX_MISTAKES = 4;

export default function GroupSort() {
  const today = useMemo(() => getTodayDate(), []);
  const [puzzle, setPuzzle] = useState<Puzzle | null>(null);
  const [order, setOrder] = useState<string[]>([]);
  const [selected, setSelected] = useState<string[]>([]);
  const [solved, setSolved] = useState<string[]>([]);
  const [mistakes, setMistakes] = useState(0);
  const [status, setStatus] = useState<"playing" | "won" | "lost">("playing");
  const [shake, setShake] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  useEffect(() => {
    fetchPuzzle(today).then((p) => {
      setPuzzle(p);
      const saved = loadState(today);
      setSolved(saved.solved);
      setMistakes(saved.mistakes);
      setStatus(saved.status);
      const solvedSet = new Set(saved.solved);
      const solvedWords: string[] = [];
      for (const title of saved.solved) {
        const cat = p.categories.find((c) => c.title === title);
        if (cat) solvedWords.push(...cat.cards.map((c) => c.content));
      }
      const remaining = p.categories
        .filter((c) => !solvedSet.has(c.title))
        .flatMap((c) => c.cards)
        .sort((a, b) => a.position - b.position)
        .map((c) => c.content);
      setOrder([...solvedWords, ...remaining]);
    });
  }, [today]);

  useEffect(() => {
    if (!puzzle) return;
    localStorage.setItem(
      "connections-state",
      JSON.stringify({ date: today, solved, mistakes, status }),
    );
  }, [today, solved, mistakes, status, puzzle]);

  const showToast = useCallback((msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 1800);
  }, []);

  const wordToCategory = useMemo(() => {
    const m = new Map<string, Category>();
    if (!puzzle) return m;
    for (const cat of puzzle.categories) {
      for (const card of cat.cards) m.set(card.content, cat);
    }
    return m;
  }, [puzzle]);

  const solvedCount = solved.length;
  const remainingStart = solvedCount * 4;

  const toggle = (word: string) => {
    if (status !== "playing") return;
    if (solved.some((t) => wordToCategory.get(word)?.title === t)) return;
    setSelected((s) => {
      if (s.includes(word)) return s.filter((w) => w !== word);
      if (s.length >= 4) return s;
      return [...s, word];
    });
  };

  const shuffle = () => {
    if (!puzzle) return;
    const head = order.slice(0, remainingStart);
    const tail = order.slice(remainingStart);
    for (let i = tail.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [tail[i], tail[j]] = [tail[j], tail[i]];
    }
    setOrder([...head, ...tail]);
  };

  const deselect = () => setSelected([]);

  const submit = () => {
    if (!puzzle || selected.length !== 4) return;
    const cats = selected.map((w) => wordToCategory.get(w)?.title);
    const allSame = cats.every((c) => c && c === cats[0]);
    if (allSame) {
      const title = cats[0]!;
      const newSolved = [...solved, title];
      setSolved(newSolved);
      const cat = puzzle.categories.find((c) => c.title === title)!;
      const catWords = cat.cards.map((c) => c.content);
      const rest = order.filter((w) => !catWords.includes(w) && !solved.flatMap((t) => puzzle.categories.find((c) => c.title === t)!.cards.map((c) => c.content)).includes(w));
      const solvedWords: string[] = [];
      for (const t of newSolved) {
        const c = puzzle.categories.find((cc) => cc.title === t)!;
        solvedWords.push(...c.cards.map((cc) => cc.content));
      }
      setOrder([...solvedWords, ...rest]);
      setSelected([]);
      if (newSolved.length === 4) {
        setStatus("won");
        setTimeout(() => showToast("Solved!"), 400);
      }
    } else {
      const counts = new Map<string, number>();
      for (const c of cats) if (c) counts.set(c, (counts.get(c) ?? 0) + 1);
      const max = Math.max(...counts.values());
      setShake(true);
      setTimeout(() => setShake(false), 500);
      showToast(max === 3 ? "One away..." : "Incorrect");
      const newMistakes = mistakes + 1;
      setMistakes(newMistakes);
      if (newMistakes >= MAX_MISTAKES) {
        setStatus("lost");
        setTimeout(() => showToast("Better luck next time"), 600);
      }
    }
  };

  if (!puzzle) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background text-foreground">
        Loading…
      </div>
    );
  }

  const solvedCategories = solved
    .map((t) => puzzle.categories.find((c) => c.title === t)!)
    .filter(Boolean);

  const unsolvedReveal = status === "lost"
    ? puzzle.categories.filter((c) => !solved.includes(c.title))
    : [];

  const remainingWords = order.slice(remainingStart);

  const difficultyIndex = (title: string) =>
    puzzle.categories.findIndex((c) => c.title === title);

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <header className="border-b border-border">
        <div className="mx-auto flex max-w-2xl items-center justify-between px-4 py-3">
          <Link to="/vocab-lab/word-drill" className="text-sm font-semibold text-muted-foreground hover:text-foreground">← Word Drill</Link>
          <h1 className="text-2xl font-bold tracking-tight text-foreground">Group Sort</h1>
          <Link to="/vocab-lab" className="text-sm font-semibold text-muted-foreground hover:text-foreground">Lab</Link>
        </div>
      </header>

      <main className="mx-auto flex w-full max-w-2xl flex-1 flex-col items-center px-4 py-6">
        <p className="mb-4 text-center text-sm text-muted-foreground">
          Create four groups of four!
        </p>

        <div className="relative w-full">
          {toast && (
            <div className="pointer-events-none absolute left-1/2 top-0 z-10 -translate-x-1/2 -translate-y-2 rounded-md bg-foreground px-4 py-2 text-sm font-bold uppercase text-background shadow-lg">
              {toast}
            </div>
          )}

          <div className={`flex flex-col gap-2 ${shake ? "animate-[shake_0.5s]" : ""}`}>
            {solvedCategories.map((cat) => {
              const di = difficultyIndex(cat.title);
              return (
                <div
                  key={cat.title}
                  className={`flex h-[88px] flex-col items-center justify-center rounded-md text-center text-foreground ${DIFFICULTY_COLORS[di]}`}
                >
                  <div className="text-sm font-extrabold uppercase tracking-wide">{cat.title}</div>
                  <div className="mt-1 text-sm font-medium uppercase tracking-wider">
                    {cat.cards.map((c) => c.content).join(", ")}
                  </div>
                </div>
              );
            })}

            {unsolvedReveal.map((cat) => {
              const di = difficultyIndex(cat.title);
              return (
                <div
                  key={cat.title}
                  className={`flex h-[88px] flex-col items-center justify-center rounded-md text-center text-foreground opacity-90 ${DIFFICULTY_COLORS[di]}`}
                >
                  <div className="text-sm font-extrabold uppercase tracking-wide">{cat.title}</div>
                  <div className="mt-1 text-sm font-medium uppercase tracking-wider">
                    {cat.cards.map((c) => c.content).join(", ")}
                  </div>
                </div>
              );
            })}

            {status !== "lost" && remainingWords.length > 0 && (
              <div className="grid grid-cols-4 gap-2">
                {remainingWords.map((w) => {
                  const isSel = selected.includes(w);
                  return (
                    <button
                      key={w}
                      onClick={() => toggle(w)}
                      className={`flex aspect-[1.6/1] items-center justify-center rounded-md px-1 text-center text-sm font-extrabold uppercase transition-transform active:scale-95 sm:text-base ${
                        isSel
                          ? "bg-[hsl(var(--connections-tile-selected))] text-background"
                          : "bg-[hsl(var(--connections-tile))] text-foreground hover:brightness-95"
                      }`}
                    >
                      {w}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        <div className="mt-6 flex items-center gap-2 text-sm text-foreground">
          <span>Mistakes remaining:</span>
          <div className="flex gap-1.5">
            {Array.from({ length: MAX_MISTAKES }).map((_, i) => (
              <span
                key={i}
                className={`h-3 w-3 rounded-full transition-opacity ${
                  i < MAX_MISTAKES - mistakes ? "bg-foreground" : "bg-foreground opacity-20"
                }`}
              />
            ))}
          </div>
        </div>

        {status === "playing" && (
          <div className="mt-6 flex flex-wrap items-center justify-center gap-2">
            <button
              onClick={shuffle}
              className="rounded-full border border-foreground px-5 py-2 text-sm font-semibold text-foreground transition-colors hover:bg-foreground hover:text-background"
            >
              Shuffle
            </button>
            <button
              onClick={deselect}
              disabled={selected.length === 0}
              className="rounded-full border border-foreground px-5 py-2 text-sm font-semibold text-foreground transition-colors hover:bg-foreground hover:text-background disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-foreground"
            >
              Deselect all
            </button>
            <button
              onClick={submit}
              disabled={selected.length !== 4}
              className="rounded-full border border-foreground bg-foreground px-5 py-2 text-sm font-semibold text-background transition-colors hover:opacity-90 disabled:opacity-40"
            >
              Submit
            </button>
          </div>
        )}

        {status !== "playing" && (
          <div className="mt-6 text-center text-base font-semibold text-foreground">
            {status === "won" ? "🎉 Nice work!" : "Better luck tomorrow."}
          </div>
        )}
      </main>

      <style>{`
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          20%, 60% { transform: translateX(-6px); }
          40%, 80% { transform: translateX(6px); }
        }
      `}</style>
    </div>
  );
}